import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { toScaled } from '../utils/invoiceSelection';
import { agreementKeys } from './agreementKeys';
import { useAgreementCashPositionQuery } from './useAgreementCashPositionQuery';
import type { AgreementCashPosition, TopUpPosting } from '../types/agreements.types';

export const TOP_UP_POLL_INTERVAL_MS = 2_000;
export const TOP_UP_POLL_DEADLINE_MS = 20_000;

export interface CashPositionWatch {
    query: UseQueryResult<AgreementCashPosition>;
    posting: TopUpPosting;
    /** The recorded top-up amount that started the watch (null for a reversal), held until it settles back to idle. */
    recordedAmount: string | null;
    startWatch: (recordedAmount: string | null) => void;
}

export function useCashPositionWatch(agreementId: string | undefined, enabled: boolean): CashPositionWatch {
    const queryClient = useQueryClient();
    const [posting, setPostingState] = useState<TopUpPosting>('idle');
    const [recordedAmount, setRecordedAmount] = useState<string | null>(null);
    const liveAgreementId = useRef<string | undefined>(undefined);
    const postingRef = useRef<TopUpPosting>('idle');
    const baseline = useRef<number | null>(null);
    const deadline = useRef<ReturnType<typeof setTimeout> | null>(null);
    const poll = useRef<ReturnType<typeof setInterval> | null>(null);
    const generation = useRef(0);
    const query = useAgreementCashPositionQuery(agreementId, enabled || posting === 'posting');

    const setPosting = useCallback((next: TopUpPosting) => {
        postingRef.current = next;
        setPostingState(next);
    }, []);

    const stopPolling = useCallback(() => {
        if (poll.current !== null) clearInterval(poll.current);
        poll.current = null;
    }, []);

    const clearDeadline = useCallback(() => {
        generation.current += 1;
        stopPolling();
        if (deadline.current !== null) clearTimeout(deadline.current);
        deadline.current = null;
    }, [stopPolling]);

    const startWatch = useCallback((amount: string | null) => {
        if (!agreementId || liveAgreementId.current !== agreementId) return;
        const held = queryClient.getQueryData<AgreementCashPosition>(agreementKeys.cashPosition(agreementId));
        if (baseline.current === null || postingRef.current !== 'posting') {
            baseline.current = held ? toScaled(held.cashPosition) : null;
        }
        clearDeadline();
        const armed = generation.current;
        const queryKey = agreementKeys.cashPosition(agreementId);
        deadline.current = setTimeout(() => {
            stopPolling();
            void queryClient.cancelQueries({ queryKey }).then(() => {
                if (generation.current !== armed) return;
                deadline.current = null;
                setPosting('timedOut');
            });
        }, TOP_UP_POLL_DEADLINE_MS);
        poll.current = setInterval(() => {
            if (generation.current !== armed || poll.current === null) return;
            void queryClient.refetchQueries({ queryKey, exact: true }, { cancelRefetch: false });
        }, TOP_UP_POLL_INTERVAL_MS);
        setRecordedAmount(amount);
        setPosting('posting');
    }, [agreementId, queryClient, clearDeadline, stopPolling, setPosting]);

    const figure = query.data ? toScaled(query.data.cashPosition) : null;
    useEffect(() => {
        if (posting === 'idle' || figure === null) return;
        if (baseline.current === null) {
            baseline.current = figure;
            return;
        }
        if (figure === baseline.current) return;
        clearDeadline();
        baseline.current = null;
        setPosting('idle');
    }, [figure, posting, clearDeadline, setPosting]);

    useEffect(() => {
        liveAgreementId.current = agreementId;
        return () => {
            liveAgreementId.current = undefined;
            clearDeadline();
            baseline.current = null;
            setPosting('idle');
        };
    }, [agreementId, clearDeadline, setPosting]);

    return { query, posting, recordedAmount: posting === 'idle' ? null : recordedAmount, startWatch };
}
